import React, { useEffect, useState, useRef } from "react";
import {
  Spinner,
  Button,
  Image,
  Badge,
  Row,
  Col,
  Card,
  Modal,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "utils/apiClient";
import useFormattedDate from "hooks/useFormatDate";
import { useAuth } from "contexts/AuthContext";

const UserIdentityDocuments = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { formatTime, formatDate, formatDateTime } = useFormattedDate();
  const { user: loggedInUser } = useAuth();

  const [idDocs, setIdDocs] = useState(null);
  const [workDocs, setWorkDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState({ id: false, work: false });
  const [preview, setPreview] = useState({
    show: false,
    url: null,
    isImage: true,
  });

  // Zoom and drag state refs for preview modal
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  // Utility to check if url is an image (basic check by extension)
  const isImage = (url) => /\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i.test(url);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const headers = {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
          "O-CLIENT-NAME": "OMC",
          "actor-id": userId,
        };

        const profileResponse = await apiClient.get(
          `/profile-service/api/v1/user-profiles/${userId}?refresh=true`,
          { headers }
        );

        if (profileResponse.data) {
          setIdDocs(profileResponse.data.userIdentityVerification);
          setWorkDocs(profileResponse.data.userWorkAuthorization);
        }
      } catch (err) {
        console.error("Error fetching documents:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [userId]);

  const handleVerify = async (type) => {
    try {
      setVerifying((prev) => ({ ...prev, [type]: true }));
      const url =
        type === "id"
          ? `/profile-service/api/v1/user-profiles/identity-verification/user/${userId}/verify`
          : `/profile-service/api/v1/user-profiles/work-authorization/user/${userId}/verify`;

      const response = await apiClient.put(url);
      if (type === "id") setIdDocs(response.data);
      else setWorkDocs(response.data);
      alert("Verification successful!");
    } catch (err) {
      alert("Failed to verify document.");
      console.error(err);
    } finally {
      setVerifying((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handlePreviewClick = (url) => {
    setPreview({ show: true, url, isImage: isImage(url) });
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleClosePreview = () => {
    setPreview({ show: false, url: null, isImage: true });
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Drag handlers for zoomed image
  const onMouseDown = (e) => {
    if (zoom === 1) return;
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  const onMouseUp = () => {
    dragging.current = false;
  };

  const onMouseMove = (e) => {
    if (!dragging.current) return;
    e.preventDefault();
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setPosition((pos) => ({
      x: pos.x + dx,
      y: pos.y + dy,
    }));
  };

  const toggleZoom = () => {
    if (zoom === 1) {
      setZoom(2);
    } else {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const DocumentSection = ({ title, docs, type }) => {
    if (!docs) {
      return (
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body>
            <h5 className="fw-semibold mb-3">{title}</h5>
            <p className="text-muted mb-0">No documents found.</p>
          </Card.Body>
        </Card>
      );
    }

    const isSameUser = loggedInUser?.id === userId;
    const documentType =
      type === "id"
        ? docs.identificationDocumentType
        : docs.workAuthorizationType;
    const documentIds = docs.idDocumentIds || [];

    return (
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <h5 className="fw-semibold mb-3">{title}</h5>
          <p>
            <strong>Type:</strong> {documentType}
          </p>
          <p>
            <strong>Uploaded Date:</strong> {formatDate(docs.uploadedOn)}
          </p>
          <p>
            <strong>Uploaded Time:</strong> {formatTime(docs.uploadedOn)}
          </p>
          <p>
            <strong>Verified:</strong>{" "}
            {docs.verified ? (
              <Badge bg="success">Yes</Badge>
            ) : (
              <Badge bg="warning">No</Badge>
            )}
          </p>
          {docs.verifiedOn && (
            <p>
              <strong>Verified On:</strong> {formatDateTime(docs.verifiedOn)}
            </p>
          )}

          <div className="d-flex flex-wrap gap-3 mb-3">
            {documentIds.map((url, i) => {
              if (isImage(url)) {
                return (
                  <Image
                    key={i}
                    src={url}
                    alt={`Document ${i + 1}`}
                    style={{
                      maxWidth: "100px",
                      maxHeight: "180px",
                      cursor: "pointer",
                      backgroundColor: "#f0f0f0",
                      objectFit: "contain",
                    }}
                    onClick={() => handlePreviewClick(url)}
                    onError={(e) => {
                      e.target.src = undefined;
                    }}
                    draggable={false}
                  />
                );
              } else {
                return (
                  <div
                    key={i}
                    style={{
                      width: "200px",
                      height: "150px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #ddd",
                      cursor: "pointer",
                      backgroundColor: "#eee",
                      userSelect: "none",
                    }}
                    onClick={() => handlePreviewClick(url)}
                    title={`View Document ${i + 1}`}
                  >
                    <span>View Document {i + 1}</span>
                  </div>
                );
              }
            })}
          </div>

          <Button
            variant="success"
            onClick={() => handleVerify(type)}
            disabled={verifying[type] || docs.verified || isSameUser}
            title={
              isSameUser ? "You cannot verify your own documents" : undefined
            }
          >
            {verifying[type] ? (
              <>
                <Spinner animation="border" size="sm" /> Verifying...
              </>
            ) : (
              "Verify Document"
            )}
          </Button>
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
      <div className="mt-3">
        <Card
          className="mb-4 shadow-sm rounded border-1"
          style={{
            boxShadow: "0 -3px 6px rgba(0, 0, 0, 0.1)",
            borderColor: "transparent",
            padding: "0.75rem 1rem",
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div style={{ minWidth: 90 }}>
              <button
                type="button"
                className="btn btn-link p-0 text-black d-flex align-items-center gap-1 text-xs"
                onClick={() => navigate(`/olitask/users/${userId}`)}
                style={{ textDecoration: "none" }}
                aria-label="Go back"
              >
                <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
              </button>
            </div>

            <div className="text-center flex-grow-1 px-3">
              <h4 className="mb-0 fw-semibold text-base">
                User Identity & Work Authorization
              </h4>
            </div>

            <div style={{ minWidth: 90 }} />
          </div>
        </Card>

        <Row>
          <Col md={6}>
            <DocumentSection
              title="Identification Documents"
              docs={idDocs}
              type="id"
            />
          </Col>
          <Col md={6}>
            <DocumentSection
              title="Work Authorization Documents"
              docs={workDocs}
              type="work"
            />
          </Col>
        </Row>

        {/* Preview Modal */}
        <Modal
          show={preview.show}
          onHide={handleClosePreview}
          size="xl"
          centered
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>Document Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body
            style={{
              height: "80vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              backgroundColor: "#000",
              cursor: zoom > 1 ? "grab" : "zoom-in",
              userSelect: "none",
            }}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onMouseMove={onMouseMove}
            onClick={toggleZoom}
          >
            {preview.isImage ? (
              <img
                src={preview.url}
                alt="Preview"
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  transform: `scale(${zoom}) translate(${
                    position.x / zoom
                  }px, ${position.y / zoom}px)`,
                  transition: dragging.current ? "none" : "transform 0.3s ease",
                  cursor: zoom > 1 ? "grab" : "zoom-in",
                  userSelect: "none",
                  borderRadius: 4,
                  boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                  backgroundColor: "#fff",
                }}
                draggable={false}
              />
            ) : (
              <iframe
                src={preview.url}
                title="Document Preview"
                style={{ width: "100%", height: "80vh", border: "none" }}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <small style={{ color: "#ccc", userSelect: "none" }}>
              Click image to zoom in/out. Drag when zoomed.
            </small>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default UserIdentityDocuments;
